import { urlWithLocale } from '@tuic/i18n';
import { toQueryString } from '@tuic/lib/src/helpers/to-query-string';
import { HOST_APP_DASHBOARD, HOST_APP_MAIN } from '@tuic/lib/urls';

import { APP_URL_SCHEMA, GET_SLASH_DOMAIN } from './config-client';

import type { Locale } from '@tuic/i18n';

export const urls = {
  pathname: (pathname?: string, queryParams?: Record<string, any>) => {
    if (pathname?.substring(0, 1) === '/') {
      pathname = pathname.substring(1);
    }

    const url = `${HOST_APP_DASHBOARD}/${pathname ?? ''}`;

    if (typeof queryParams === 'undefined') {
      return url;
    }

    return `${url}?${toQueryString(queryParams)}`;
  },

  assignVirtualToken: (params: {
    locale?: Locale;
    token: string;
    bookingCode: string;
  }) => {
    return `/api/assign-virtual-token?${toQueryString(params)}`;
  },

  index: (params: {
    locale?: Locale;
    op?: 'addTrip';
    firstName?: string;
    lastName?: string;
    bookingCode?: string;
  }) => {
    const queryString = toQueryString({
      op: params.op,
      firstName: params.firstName,
      lastName: params.lastName,
      bookingCode: params.bookingCode,
    });

    const url = urlWithLocale(params.locale, '/');

    if (queryString === '') {
      return url;
    }

    return `${url}?${queryString}`;
  },

  booking: (params: {
    bookingCode: string;
    locale?: Locale;
    absolute?: boolean;
  }) => {
    const url = urlWithLocale(params.locale, `/${params.bookingCode}`);

    return params.absolute ? HOST_APP_DASHBOARD + url : url;
  },

  contentBlockPage: (params: {
    locale?: Locale;
    bookingCode: string;
    slug: string;
  }) => {
    return urlWithLocale(
      params.locale,
      `/${params.bookingCode}/${params.slug}`,
    );
  },

  contentBlockPageAbsolute: (params: {
    locale?: Locale;
    bookingCode: string;
    slug: string;
  }) => {
    return HOST_APP_DASHBOARD + urls.contentBlockPage(params);
  },

  downloadProxy: (params: { url: string }) => {
    return `/api/download?u=` + encodeURIComponent(params.url);
  },

  go: (params: { slug: string }) => `/go/${params.slug}`,

  mxpIframe: (params: {
    locale?: Locale;
    bookingCode: string;
    mxpPath: string;
  }) => {
    const searchParams = new URLSearchParams({
      mxp: params.mxpPath,
    });
    return urlWithLocale(
      params.locale,
      `/${params.bookingCode}?${searchParams.toString()}`,
    );
  },

  appSchema: {
    signInSuccess: (params: {
      locale?: Locale;
      bookingCode: string;
      bookingCodeToken: string;
    }) => {
      return `${APP_URL_SCHEMA}://auth/booking-code/success?${toQueryString(params)}`;
    },
  },

  onBoardInternetEndpoint: (params: {
    locale?: Locale;
    bookingCode: string;
  }) => {
    return `/api/bookings/${params.bookingCode}/internet?locale=${params.locale}`;
  },

  onBoardInternet: (params: {
    locale?: Locale;
    queryParams?: Record<string, string | null | undefined>;
  }) => {
    const url = new URL(GET_SLASH_DOMAIN);

    if (params.locale) {
      url.searchParams.append('lc', params.locale);
    }

    const { queryParams } = params;

    if (typeof queryParams === 'undefined') {
      return url.toString();
    }

    url.hash = `/?${toQueryString(queryParams)}`;

    return url.toString();
  },

  privacyPolicy: (params: { locale: Locale }) => HOST_APP_MAIN + '/datenschutz',

  signInWithBookingCode: (params: {
    locale?: Locale;
    app?: string;
    intended?: string;
    appSchemaRedirect?: boolean;
  }) => {
    const { locale, appSchemaRedirect, ...rest } = params;

    const url = urlWithLocale(locale, '/sign-in');

    const newParams: Record<string, any> = {
      ...rest,
    };

    if (appSchemaRedirect === false) {
      newParams.appSchemaRedirect = '0';
    }

    if (Object.values(newParams).length === 0) {
      return url;
    }

    return `${url}?${toQueryString(newParams)}`;
  },

  signInWithBookingCodeApi: () => '/api/sign-in',

  signOutWithBookingCode: (params: { locale?: Locale }) => {
    return `/api/sign-out?${toQueryString(params)}`;
  },

  trips: (params: { locale?: Locale }) => urlWithLocale(params.locale, '/'),
  tripsArchive: (params: { locale?: Locale }) =>
    urlWithLocale(params.locale, '/archive'),

  highlightsPage: (params: { locale?: Locale; bookingCode: string }) => {
    return urlWithLocale(
      params.locale,
      `/${params.bookingCode}/journey-highlights`,
    );
  },

  travelInformation: (params: { locale?: Locale; bookingCode: string }) => {
    return urlWithLocale(
      params.locale,
      `/${params.bookingCode}/travel-information`,
    );
  },

  travelItinerary: (params: {
    locale: Locale;
    bookingCode: string;
    travelDirection: 'arrival' | 'departure';
  }) =>
    urlWithLocale(
      params.locale,
      `/${params.bookingCode}/travel-itinerary/${params.travelDirection}`,
    ),

  //TODO: Use Query Strings for modals -- keep URLs lean & clean
  flightTimeline: (params: {
    locale?: Locale;
    bookingCode?: string;
    travelDirection?: 'arrival' | 'departure';
    flightDirection?: 'inbound' | 'outbound';
    travelerId?: string;
  }) =>
    urlWithLocale(
      params.locale,
      `/${params.bookingCode}/travel-itinerary/${params.travelDirection}/modal/${params.flightDirection}/${params.travelerId}/flight-timeline`,
    ),

  //TODO: Use Query Strings for modals -- keep URLs lean & clean
  flightDetails: (params: {
    locale: Locale;
    bookingCode: string;
    travelDirection: 'arrival' | 'departure';
    flightNumber: string;
    travelerId: string;
    flightDirection: 'inbound' | 'outbound';
  }) =>
    urlWithLocale(
      params.locale,
      `/${params.bookingCode}/travel-itinerary/${params.travelDirection}/modal/${params.flightDirection}/${params.travelerId}/flight-details/${params.flightNumber}`,
    ),

  survey: (params: {
    locale: Locale;
    surveyId: number;
    apiToken: string;
    userAge?: number;
  }) => {
    const searchParams = new URLSearchParams({
      surveyId: params.surveyId.toString(),
      apiToken: params.apiToken,
      ...(params.userAge && { userAge: params.userAge.toString() }),
    });
    return urlWithLocale(params.locale, `/survey?${searchParams.toString()}`);
  },
};
